/**
 * Month-End Forecast — A3's estimate and the arithmetic behind it.
 *
 * Follows the "September Month-End Build" design guide: hero figures with a
 * certainty meter, the headline stats, the build waterfall, the cross-checks,
 * the account disposition, the decay curve, the numbers as a table, the flags,
 * and — the part the guide's customer table asks for — this month's layers
 * carried down to each end user and each ship-to.
 *
 * Three rules the page keeps:
 *
 * * **Nothing is computed here.** Every figure arrives in the payload. `derive`
 *   owns the arithmetic and the forecast itself is the agent's stored total.
 * * **Expected is allocated, not modelled.** The agent publishes one month total
 *   for expected orders; the Expected column spreads it across the accounts that
 *   can still order, so it sums back to the agent's figure exactly. A single
 *   account's figure is indicative; the total is the agent's.
 * * **The reconciliation is on the page, not in a comment.** Invoiced ties to
 *   the agent exactly; the open book does not and cannot, because it drains as
 *   orders ship. Both are stated.
 */

import { Fragment, useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

import { BuildWaterfall, type BuildStep } from './BuildWaterfall'
import { DecayChart, type DecayPoint } from './DecayChart'
import {
  gallons,
  money,
  NONE,
  num,
  rate,
  signedGallons,
  signedMoney,
  signedPct,
  type Unit,
} from './format'

const ALL = '(all)'

/** How many account rows the detail table renders before it asks for a filter. */
const ACCOUNT_ROW_LIMIT = 200

/**
 * A signed variance in the reader's chosen unit.
 *
 * Gallons and dollars are separate stored figures, never one derived from the
 * other by a blended rate, so a panel with no dollars of its own keeps showing
 * gallons and says so rather than inventing a price.
 */
function signedInUnit(gal: number | null, usd: number | null, unit: Unit): string {
  return unit === 'usd' ? signedMoney(usd) : signedGallons(gal)
}

/** A layer figure in the chosen unit, or a dash where that unit has no figure. */
function layerInUnit(gal: Cell | undefined, usd: Cell | undefined, unit: Unit): string {
  return unit === 'usd' ? money(num(usd)) : gallons(num(gal))
}

function unitOf(value: unknown): Unit {
  return value === 'usd' ? 'usd' : 'gal'
}

/** One figure in the unit the reader picked, or a dash where none exists. */
function inUnit(gal: number | null, usd: number | null, unit: Unit): string {
  return unit === 'usd' ? money(usd) : gallons(gal)
}

function Stat({
  label,
  value,
  detail,
}: {
  label: string
  value: string
  detail?: string | undefined
}) {
  return (
    <div className="mef-stat">
      <div className="mef-stat-key">{label}</div>
      <div className="mef-stat-value">{value}</div>
      {detail !== undefined && <div className="mef-stat-detail">{detail}</div>}
    </div>
  )
}

export function MonthEndForecast({ result, values, setParam }: CustomReportProps) {
  const headline = dataset(result, 'headline')[0]

  const buildup = dataset(result, 'buildup')
  const planSummary = dataset(result, 'plan_summary')
  const reconciliation = dataset(result, 'reconciliation')
  const customerRows = dataset(result, 'customers')

  // Which stored plan the table is measured against. The label matters: BUD and
  // the quarterly revision differ materially for the same month, so a column
  // headed just "Plan" would be ambiguous the moment anyone compares two
  // screenshots.
  const planChoice = typeof values['plan'] === 'string' ? values['plan'] : 'revision'
  // The column is the forecast this month is measured against; the stored
  // version's name (BUD, 9+3) qualifies it rather than replacing it, because
  // "Revision" on its own says nothing about what the number is.
  const planVersion =
    planChoice === 'budget' ? 'BUD' : (text(customerRows[0]?.revision_label) ?? '')
  const planLabel =
    planChoice === 'none' ? null : planVersion === '' ? 'Forecast' : `Forecast (${planVersion})`

  const breaks = dataset(result, 'breaks')
  const asOf = dataset(result, 'as_of')
  const crosscheck = dataset(result, 'crosscheck')
  const disposition = dataset(result, 'disposition')
  const decay = dataset(result, 'decay')
  const pipeline = dataset(result, 'pipeline')
  const flags = dataset(result, 'flags')
  const accounts = dataset(result, 'primary')

  const unit = unitOf(values.unit)
  const [openCustomer, setOpenCustomer] = useState<string | null>(null)
  const selectedAccounts = typeof values.accounts === 'string' ? values.accounts : ALL

  if (headline === undefined) {
    return <Empty label="This run carried no forecast" />
  }

  const forecastGal = num(headline.forecast_gal)
  const forecastUsd = num(headline.forecast_usd)
  const bandLow = num(headline.band_low_gal)
  const bandMid = num(headline.band_mid_gal)
  const bandHigh = num(headline.band_high_gal)
  const bandLowUsd = num(headline.band_low_usd)
  const bandMidUsd = num(headline.band_mid_usd)
  const bandHighUsd = num(headline.band_high_usd)
  const bandErrPct = num(headline.band_err_pct)
  const bandBasisDays = num(headline.band_basis_days)
  const bandNote = text(headline.band_note)
  const bandThin = text(headline.band_thin) === 'yes'
  const priorYear = num(headline.prior_year_gal)
  const yoy = num(headline.yoy_pct)
  const knownGal = num(headline.known_gal)
  const knownUsd = num(headline.known_usd)
  const knownPct = num(headline.known_pct)
  const valuePerGal = num(headline.value_per_gal)
  const customers = num(headline.customers)
  const sites = num(headline.sites)
  const pairs = num(headline.pairs)
  const daysToClose = num(headline.days_to_close)

  const steps: BuildStep[] = buildup.map((row) => ({
    step: text(row.step) ?? '',
    layer: text(row.layer) ?? '',
    gallons: num(row.gallons),
    sharePct: num(row.share_pct),
  }))
  const decayPoints: DecayPoint[] = decay.flatMap((row) => {
    const day = text(row.day)
    const value = num(row.accounts)
    return day === null || value === null ? [] : [{ day, accounts: value }]
  })

  const barStates = disposition.filter((row) => text(row.in_bar) === 'yes')
  const otherStates = disposition.filter((row) => text(row.in_bar) !== 'yes')
  const population = barStates.reduce((sum, row) => sum + count(row.sites), 0)

  const modelledPct = knownPct === null ? null : 100 - knownPct

  /** Money where the reader asked for it, gallons otherwise.
   *
   * Takes `Cell | undefined` because `noUncheckedIndexedAccess` makes every
   * lookup on a keyed row optional; `num` already collapses both to null.
   */
  const layer = (gal: Cell | undefined, usd: Cell | undefined) =>
    unit === 'usd' ? money(num(usd)) : gallons(num(gal))

  return (
    <div className="bespoke-report mef">
      <section className="mef-hero">
        <div className="mef-figure">
          <div className="mef-stat-key">Forecast · {unit === 'usd' ? 'dollars' : 'gallons'}</div>
          <div className="mef-figure-main">{inUnit(forecastGal, forecastUsd, unit)}</div>
          <div className="mef-figure-sub">
            {unit === 'usd' ? gallons(forecastGal) : money(forecastUsd)} ·{' '}
            <span className={yoy !== null && yoy < 0 ? 'mef-down' : 'mef-up'}>
              {signedPct(yoy)}
            </span>{' '}
            vs prior year {gallons(priorYear)}
          </div>
        </div>

        {/* The forecast has run low in every backtested band but the closed one,
            so the figure to quote is the corrected range, not the point. The
            unadjusted number stays in the card to its left: this one is derived
            from it and a reader has to be able to see both. */}
        {bandMid !== null && (
          <div className="mef-figure mef-figure-alt">
            <div className="mef-stat-key">Likely range · bias-adjusted</div>
            <div className="mef-figure-main">{inUnit(bandMid, bandMidUsd, unit)}</div>
            <div className="mef-figure-sub">
              {unit === 'usd'
                ? `${money(bandLowUsd)} – ${money(bandHighUsd)}`
                : `${gallons(bandLow)} – ${gallons(bandHigh)}`}
              {bandErrPct === null ? '' : ` · corrected ${signedPct(-bandErrPct)}`}
            </div>
          </div>
        )}

        <div className="mef-meter-wrap">
          <div className="mef-stat-key">Certainty of the forecast</div>
          <div className="mef-meter">
            {steps
              .filter((s) => s.layer !== 'total' && s.gallons !== null)
              .map((s) => (
                <div
                  key={s.layer}
                  className={`mef-meter-seg mef-meter-${s.layer}`}
                  style={{ flexGrow: s.gallons ?? 0 }}
                  title={`${s.step} · ${gallons(s.gallons)}`}
                >
                  <span>{s.sharePct === null ? '' : `${String(Math.round(s.sharePct))}%`}</span>
                </div>
              ))}
          </div>
          <div className="mef-meter-foot">
            <span>
              {inUnit(knownGal, knownUsd, unit)} known
              {knownPct === null ? '' : ` (${knownPct.toFixed(1)}%)`} ·{' '}
              {modelledPct === null ? NONE : `${modelledPct.toFixed(1)}% modelled`}
            </span>
            <span>{daysToClose === null ? '' : `${String(daysToClose)} days to close`}</span>
          </div>
          {bandNote !== null && (
            <p className={bandThin ? 'mef-band-note mef-band-thin' : 'mef-band-note'}>
              {bandNote}
              {bandBasisDays === null
                ? ''
                : ` Measured over ${String(bandBasisDays)} replayed days${
                    bandThin ? ' — under twelve, so read it as directional.' : '.'
                  }`}
            </p>
          )}
        </div>
      </section>

      <Card
        title="What each figure is as of"
        subtitle="Four clocks run at different speeds here, so there is no single as-of date to print. The run time and the data date are separate lines on purpose — A3 has run twice over one watermark before now, and a newer run is not newer data."
      >
        <table className="mef-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>As of</th>
              <th>Age</th>
              <th>What it means</th>
            </tr>
          </thead>
          <tbody>
            {asOf.map((row) => (
              <tr key={text(row.source) ?? ''}>
                <td className="mef-field">{text(row.source)}</td>
                <td className="mef-num">{text(row.as_of)}</td>
                <td className="mef-num">{text(row.age)}</td>
                <td>
                  <span className="mef-cnote">{text(row.note)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        title="Volume and value do not move together"
        subtitle="Gallons are exact. Dollars are actual FOB on the shipped and committed layers, with the modelled layer priced at a flat rate — so the dollar total is a mixed basis and has no prior-year figure stored to compare against."
      >
        <div className="mef-stats">
          <Stat
            label="Gallons"
            value={gallons(forecastGal)}
            detail={`${signedPct(yoy)} vs ${gallons(priorYear)} last year`}
          />
          <Stat
            label="Dollars"
            value={money(forecastUsd)}
            detail="Mixed basis; no prior year stored"
          />
          <Stat
            label="Value per gallon"
            value={rate(valuePerGal)}
            detail="Forecast dollars over forecast gallons"
          />
          <Stat
            label="Customers"
            value={customers === null ? NONE : customers.toLocaleString()}
            detail={
              sites === null || pairs === null
                ? undefined
                : `${sites.toLocaleString()} ship-tos, ${pairs.toLocaleString()} customer × ship-to pairs`
            }
          />
        </div>
      </Card>

      <Card
        title="The build"
        subtitle="Shipped is invoiced fact. The order book is placed orders taken at face value. Only the third layer is modelled — each account's own cadence, stopped when its ship date would fall past month end. Drawn in gallons: the shipped/committed dollar split is not stored."
      >
        <ul className="mef-legend">
          <li>
            <span className="mef-swatch mef-meter-shipped" /> Shipped · invoiced
          </li>
          <li>
            <span className="mef-swatch mef-meter-committed" /> Committed · in the book
          </li>
          <li>
            <span className="mef-swatch mef-meter-predicted" /> Predicted · modelled
          </li>
        </ul>
        <BuildWaterfall
          steps={steps}
          priorYear={priorYear}
          bandLow={bandLow}
          bandMid={bandMid}
          bandHigh={bandHigh}
        />
      </Card>


      {planSummary.length > 0 && (
        <Card
          title="The month against plan"
          subtitle={
            'The projected month against each stored plan, both sides complete. This is not ' +
            'the sum of the customer rows below: the plan total includes placeholder lines ' +
            'and any ship-to the dimension does not carry, which cannot be attributed to a ' +
            'customer but are still part of the number the business agreed.'
          }
        >
          <div className="mef-plan-grid">
            {planSummary.map((row) => {
              const variance = num(row.variance_gallons)
              const behind = (variance ?? 0) < 0
              return (
                <article className="mef-plan-card" key={text(row.basis) ?? ''}>
                  <h3 className="mef-plan-title">{text(row.basis)}</h3>
                  <p className={behind ? 'mef-plan-var mef-down' : 'mef-plan-var mef-up'}>
                    {signedInUnit(variance, num(row.variance_fob), unit)}
                    <span className="mef-plan-pct">
                      {num(row.variance_pct) === null
                        ? ''
                        : ` ${signedPct(num(row.variance_pct))}`}
                    </span>
                  </p>
                  <p className="mef-plan-money">
                    {unit === 'usd'
                      ? `${signedGallons(variance)} gal`
                      : signedMoney(num(row.variance_fob))}
                  </p>
                  <dl className="mef-plan-facts">
                    <div>
                      <dt>{text(row.figure_label) ?? 'Plan'}</dt>
                      <dd>{layerInUnit(row.plan_gallons, row.plan_fob, unit)}</dd>
                    </div>
                    <div>
                      <dt>Projected</dt>
                      <dd>{layerInUnit(row.projected_gallons, row.projected_fob, unit)}</dd>
                    </div>
                    <div>
                      <dt>Attributed</dt>
                      <dd>{gallons(num(row.attributed_gallons))} gal</dd>
                    </div>
                    <div>
                      <dt>Unplaced</dt>
                      <dd>{gallons(num(row.unplaced_gallons))} gal</dd>
                    </div>
                  </dl>
                  <p className="mef-plan-note">{text(row.note)}</p>
                </article>
              )
            })}
          </div>
        </Card>
      )}

      <Card
        title="Do the rebuilt layers match the agent?"
        subtitle="Invoiced and open are rebuilt per account from the order data using the agent's own scope rules. This is that rebuild checked against what the agent stored."
      >
        <table className="mef-table">
          <thead>
            <tr>
              <th>Layer</th>
              <th className="mef-num">Agent stored (gal)</th>
              <th className="mef-num">Attributed here (gal)</th>
              <th className="mef-num">Difference (gal)</th>
              <th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {reconciliation.map((row) => {
              const verdict = text(row.verdict) ?? ''
              return (
                <tr key={text(row.layer) ?? ''}>
                  <td>
                    <span className="mef-cname">{text(row.layer)}</span>
                    <span className="mef-cnote">{text(row.reason)}</span>
                  </td>
                  <td className="mef-num">{gallons(num(row.stored))}</td>
                  <td className="mef-num">{gallons(num(row.attributed))}</td>
                  <td className="mef-num">{signedGallons(num(row.difference))}</td>
                  <td>
                    <span
                      className={`mef-pill${verdict === 'drifted' ? ' mef-pill-risk' : ''}`}
                    >
                      {verdict}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>

      <Card
        title="End users: this month, by layer"
        subtitle="Invoiced and open are rebuilt per customer from the order data; Expected is the agent's month total allocated across the accounts that can still order. Prior year is the same month last year in full, from the same view and credit rule — it does not reproduce the agent's own prior-year total, so compare customers with each other rather than reading it as the month. Ranked by the projected month. The plan column is the stored forecast for this month mapped to each customer through customersku_v2_latest, falling back to the ship-to dimension where that map has no row — 98% of the plan lands on a customer already on this page; what could not be placed is listed under As of."
      >
        <div className="mef-scroll mef-scroll-tall">
          <table className="mef-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th className="mef-num">Ship-tos</th>
                <th className="mef-num">Invoiced</th>
                <th className="mef-num">Open</th>
                <th className="mef-num">Expected</th>
                <th className="mef-num">Projected</th>
                <th className="mef-num">Prior year</th>
                <th className="mef-num">vs PY</th>
                {planLabel !== null && <th className="mef-num">{planLabel}</th>}
                {planLabel !== null && <th className="mef-num">vs plan</th>}
                <th className="mef-num">Share</th>
                <th className="mef-num">Lead</th>
                <th className="mef-num">Can still order</th>
                <th className="mef-num">Breaks</th>
                <th>Cutoff position</th>
              </tr>
            </thead>
            <tbody>
              {customerRows.map((row) => {
                const open = count(row.sites_open)
                const past = count(row.sites_past)
                const noLead = count(row.sites_no_lead)
                const lead = num(row.lead_p50_weighted)
                const locked = open === 0 && past > 0
                const customerName = text(row.end_user) ?? ''
                const isOpen = openCustomer === customerName
                const sites = isOpen
                  ? accounts.filter((site) => text(site.end_user) === customerName)
                  : []
                return (
                  <Fragment key={customerName}>
                  <tr className={isOpen ? 'mef-parent is-open' : undefined}>
                    <td>
                      <button
                        type="button"
                        className="mef-disclose"
                        aria-expanded={openCustomer === customerName}
                        onClick={() => {
                          setOpenCustomer((current: string | null) =>
                            current === customerName ? null : customerName,
                          )
                        }}
                      >
                        <span className="mef-caret" aria-hidden="true">
                          {openCustomer === customerName ? '▾' : '▸'}
                        </span>
                        <span className="mef-cname">{customerName}</span>
                      </button>
                    </td>
                    <td className="mef-num">{count(row.sites).toLocaleString()}</td>
                    <td className="mef-num">{layer(row.invoiced_gallons, row.invoiced_fob)}</td>
                    <td className="mef-num">{layer(row.open_gallons, row.open_fob)}</td>
                    <td className="mef-num mef-allocated">
                      {layerInUnit(row.forecast_gallons, row.forecast_fob, unit)}
                    </td>
                    <td className="mef-num">
                      <b>{layerInUnit(row.projected_gallons, row.projected_fob, unit)}</b>
                    </td>
                    <td className="mef-num">
                      {layerInUnit(row.prior_year_gallons, row.prior_year_fob, unit)}
                    </td>
                    <td className="mef-num">
                      {num(row.vs_prior_pct) === null ? (
                        <span className="mef-absent" title="no volume last September">
                          {NONE}
                        </span>
                      ) : (
                        <span className={(num(row.vs_prior_pct) ?? 0) < 0 ? 'mef-down' : 'mef-up'}>
                          {signedPct(num(row.vs_prior_pct))}
                        </span>
                      )}
                    </td>
                    {planLabel !== null && (
                      <td className="mef-num">
                        {layerInUnit(row.plan_gallons, row.plan_fob, unit)}
                      </td>
                    )}
                    {planLabel !== null && (
                      <td className="mef-num">
                        {num(row.vs_plan_pct) === null ? (
                          <span
                            className="mef-absent"
                            title="this customer is not in the plan for this month"
                          >
                            {NONE}
                          </span>
                        ) : (
                          // Percentage on top, gallons underneath: the ratio is
                          // what ranks a customer and the absolute is what the
                          // conversation is about, and two columns for that
                          // would not fit.
                          <span
                            className={
                              (num(row.vs_plan_gallons) ?? 0) < 0 ? 'mef-down' : 'mef-up'
                            }
                          >
                            {signedPct(num(row.vs_plan_pct))}
                            <span className="mef-sub">
                              {signedInUnit(
                                num(row.vs_plan_gallons),
                                num(row.vs_plan_fob),
                                unit,
                              )}
                            </span>
                          </span>
                        )}
                      </td>
                    )}
                    <td className="mef-num">
                      {num(row.share_pct) === null
                        ? NONE
                        : `${(num(row.share_pct) ?? 0).toFixed(1)}%`}
                    </td>
                    <td className="mef-num">{lead === null ? NONE : `${lead.toFixed(1)}d`}</td>
                    <td className="mef-num">
                      <span className={locked ? 'mef-pill mef-pill-risk' : 'mef-pill'}>
                        {open.toLocaleString()} of {count(row.sites).toLocaleString()}
                      </span>
                    </td>
                    <td className="mef-num">
                      {count(row.sites_broken) === 0 ? (
                        <span className="mef-absent">{NONE}</span>
                      ) : (
                        <span
                          className="mef-pill mef-pill-risk"
                          title={`${gallons(num(row.gallons_at_risk))} gal at risk`}
                        >
                          {count(row.sites_broken).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td>
                      <div
                        className="mef-split"
                        title={`${String(open)} can still order · ${String(past)} past cutoff${noLead > 0 ? ` · ${String(noLead)} no lead time` : ''}`}
                      >
                        {open > 0 && <i className="mef-split-open" style={{ flexGrow: open }} />}
                        {past > 0 && <i className="mef-split-past" style={{ flexGrow: past }} />}
                        {noLead > 0 && (
                          <i className="mef-split-unknown" style={{ flexGrow: noLead }} />
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen &&
                    sites.map((site) => (
                      <tr className="mef-child" key={text(site.ship_to_number) ?? ''}>
                        <td className="mef-child-name">
                          <span className="mef-cname">{text(site.ship_to_number)}</span>
                          <span className="mef-cnote">{text(site.cbsa_title) ?? ''}</span>
                        </td>
                        <td className="mef-num">1</td>
                        <td className="mef-num">
                          {layerInUnit(site.invoiced_gallons, site.invoiced_fob, unit)}
                        </td>
                        <td className="mef-num">
                          {layerInUnit(site.open_gallons, site.open_fob, unit)}
                        </td>
                        <td className="mef-num mef-allocated">
                          {layerInUnit(site.forecast_gallons, site.forecast_fob, unit)}
                        </td>
                        <td className="mef-num">
                          {layerInUnit(site.projected_gallons, site.projected_fob, unit)}
                        </td>
                        <td className="mef-num">
                          {layerInUnit(site.prior_year_pair_gallons, site.prior_year_pair_fob, unit)}
                        </td>
                        <td className="mef-num" />
                        {planLabel !== null && (
                          <td className="mef-num">
                            {layerInUnit(
                              planChoice === 'budget' ? site.budget_gallons : site.revision_gallons,
                              planChoice === 'budget' ? site.budget_fob : site.revision_fob,
                              unit,
                            )}
                          </td>
                        )}
                        {planLabel !== null && <td className="mef-num" />}
                        <td className="mef-num" />
                        <td className="mef-num">
                          {num(site.lead_p50) === null
                            ? NONE
                            : `${(num(site.lead_p50) ?? 0).toFixed(1)}d`}
                        </td>
                        <td className="mef-num">
                          <span className="mef-pill">{text(site.cutoff_status) ?? NONE}</span>
                        </td>
                        <td className="mef-num" />
                        <td className="mef-child-note">{text(site.status_note) ?? ''}</td>
                      </tr>
                    ))}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="mef-foot">
          Top {customerRows.length.toLocaleString()} of{' '}
          {customers === null ? NONE : customers.toLocaleString()} customers, covering{' '}
          {customerRows.reduce((sum, row) => sum + (num(row.share_pct) ?? 0), 0).toFixed(1)}% of
          the projected month. Computed over every account, so the filter below does not narrow
          it. Expected figures are an allocation of the agent's total, shaded to say so.
        </p>
      </Card>

      <Card
        title={`Cadence breaks on these accounts (${breaks.length.toLocaleString()} open)`}
        subtitle="From the A2 agent, matched to this page on both customer and ship-to — A2 watches the customer at a location, not the location. It measures purely on order date, so an order placed and not yet shipped already counts as activity. Ranked by A2's own gallons at risk."
      >
        {breaks.length === 0 ? (
          <Empty label="No open cadence breaks on the accounts in this forecast" />
        ) : (
          <div className="mef-scroll mef-scroll-tall">
            <table className="mef-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Ship-to</th>
                  <th>Break</th>
                  <th className="mef-num">Days silent</th>
                  <th className="mef-num">Cycles missed</th>
                  <th className="mef-num">Last order</th>
                  <th className="mef-num">At risk (gal)</th>
                  <th className="mef-num">Invoiced</th>
                  <th className="mef-num">Open</th>
                </tr>
              </thead>
              <tbody>
                {breaks.map((row) => {
                  const severity = text(row.severity) ?? ''
                  const loud = severity === 'critical' || severity === 'high'
                  const shared = text(row.shared_note)
                  return (
                    <tr
                      key={`${text(row.end_user) ?? ''}|${text(row.ship_to_number) ?? ''}`}
                    >
                      <td>
                        <span className="mef-cname">{text(row.end_user)}</span>
                      </td>
                      <td>
                        <span>{text(row.ship_to_number)}</span>
                        {shared !== null && shared !== '' && (
                          <span className="mef-cnote">{shared}</span>
                        )}
                      </td>
                      <td>
                        <span className={`mef-pill${loud ? ' mef-pill-risk' : ''}`}>
                          {text(row.kind)}
                        </span>
                      </td>
                      <td className="mef-num">
                        {num(row.days_silent) === null
                          ? NONE
                          : `${(num(row.days_silent) ?? 0).toFixed(0)}d`}
                      </td>
                      <td className="mef-num">
                        {num(row.cycles_missed) === null
                          ? NONE
                          : (num(row.cycles_missed) ?? 0).toFixed(1)}
                      </td>
                      <td className="mef-num">{text(row.last_order_date) ?? NONE}</td>
                      <td className="mef-num">
                        <b>{gallons(num(row.gallons_at_risk))}</b>
                      </td>
                      <td className="mef-num">{gallons(num(row.invoiced_gallons))}</td>
                      <td className="mef-num">{gallons(num(row.open_gallons))}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Ship-tos: this month, cadence and where each one stands"
        subtitle={`One row per customer × ship-to. Invoiced and open are this month's facts for that pair; Expected is that pair's allocated share of the agent's month total, and Orders left is how many more times its cadence fits before its cutoff. ${accounts.length.toLocaleString()} shown${selectedAccounts === ALL ? '' : ' — filtered'}.`}
        actions={
          <button
            type="button"
            className="mef-link"
            onClick={() => {
              setParam('accounts', selectedAccounts === ALL ? 'active' : ALL)
            }}
          >
            {selectedAccounts === ALL ? 'Only those with volume' : 'Show all accounts'}
          </button>
        }
      >
        <div className="mef-scroll mef-scroll-tall">
          <table className="mef-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Ship-to</th>
                <th className="mef-num">Invoiced</th>
                <th className="mef-num">Open</th>
                <th className="mef-num">Expected</th>
                <th className="mef-num">Projected</th>
                <th className="mef-num">Orders left</th>
                <th>Cadence</th>
                <th className="mef-num">Lead</th>
                <th>Cadence break</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {accounts.slice(0, ACCOUNT_ROW_LIMIT).map((row: Keyed) => {
                const status = text(row.cutoff_status) ?? ''
                const anchor = text(row.anchor_day)
                const cadenceClass = text(row.cadence_class) ?? ''
                const strength = num(row.anchor_strength)
                const confidence = text(row.cadence_confidence) ?? 'established'
                const breakStatus = text(row.break_status) ?? ''
                return (
                  <tr key={`${text(row.end_user) ?? ''}|${text(row.ship_to_number) ?? ''}`}>
                    <td>{text(row.end_user) ?? NONE}</td>
                    <td>
                      <span className="mef-cname">{text(row.ship_to_number)}</span>
                      <span className="mef-cnote">{text(row.cbsa_title) ?? ''}</span>
                    </td>
                    <td className="mef-num">{layer(row.invoiced_gallons, row.invoiced_fob)}</td>
                    <td className="mef-num">{layer(row.open_gallons, row.open_fob)}</td>
                    <td className="mef-num mef-allocated">
                      {layerInUnit(row.forecast_gallons, row.forecast_fob, unit)}
                    </td>
                    <td className="mef-num">
                      <b>{layerInUnit(row.projected_gallons, row.projected_fob, unit)}</b>
                    </td>
                    <td className="mef-num">{count(row.order_opportunities).toLocaleString()}</td>
                    <td>
                      {cadenceClass}
                      {anchor === null ? '' : ` · ${anchor}`}
                      {strength === null ? '' : ` ${strength.toFixed(0)}%`}
                      {confidence !== 'established' && (
                        <span className="mef-cnote">{confidence} — treat the class with care</span>
                      )}
                    </td>
                    <td className="mef-num">
                      {num(row.lead_p50) === null ? NONE : `${(num(row.lead_p50) ?? 0).toFixed(0)}d`}
                    </td>
                    <td>
                      {breakStatus === '' ? (
                        <span className="mef-absent">{NONE}</span>
                      ) : (
                        <span className="mef-pill mef-pill-risk">{breakStatus}</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`mef-pill${status === 'Past cutoff' ? ' mef-pill-risk' : ''}`}
                      >
                        {status}
                      </span>
                      <span className="mef-cnote">{text(row.status_note)}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {accounts.length > ACCOUNT_ROW_LIMIT && (
          <p className="mef-foot">
            Showing the {ACCOUNT_ROW_LIMIT} largest of {accounts.length.toLocaleString()} by volume
            this month. Narrow with the Accounts filter above.
          </p>
        )}
      </Card>

      <div className="mef-two">
        <Card
          title="Other estimates of the same month"
          subtitle="One of these is not a cross-check, and the page says which."
        >
          <table className="mef-table">
            <thead>
              <tr>
                <th>Method</th>
                <th className="mef-num">Gallons</th>
                <th className="mef-num">vs forecast</th>
                <th>Cross-check</th>
              </tr>
            </thead>
            <tbody>
              {crosscheck.map((row) => {
                const valid = text(row.valid) === 'yes'
                return (
                  <tr key={text(row.method) ?? ''}>
                    <td>
                      <span className="mef-cname">{text(row.method)}</span>
                      <span className="mef-cnote">{text(row.reading)}</span>
                    </td>
                    <td className="mef-num">{gallons(num(row.gallons))}</td>
                    <td className="mef-num">{signedPct(num(row.vs_forecast_pct))}</td>
                    <td>
                      <span className={`mef-pill${valid ? '' : ' mef-pill-risk'}`}>
                        {valid ? 'valid' : 'not valid'}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>

        <Card
          title="Certainty closes in"
          subtitle={`Ship-tos that could order on a given day and still ship inside the month, over the ${sites === null ? '' : sites.toLocaleString()} this report models — a wider population than the agent's own ${String(population)}.`}
        >
          <DecayChart points={decayPoints} />
        </Card>
      </div>

      <Card
        title={`Where the ${String(population)} modelled accounts sit`}
        subtitle="At the agent's watermark. The first three states partition the population; the rest overlap it or count orders, so they are listed separately rather than stacked."
      >
        <div className="mef-dispo">
          {barStates.map((row) => (
            <div
              key={text(row.state) ?? ''}
              className={`mef-dispo-seg mef-dispo-${(text(row.state) ?? '').toLowerCase().replace(/\s+/g, '-')}`}
              style={{ flexGrow: count(row.sites) }}
              title={`${text(row.state) ?? ''} · ${count(row.sites).toLocaleString()} sites`}
            />
          ))}
        </div>
        <div className="mef-stats">
          {barStates.concat(otherStates).map((row) => (
            <Stat
              key={text(row.state) ?? ''}
              label={text(row.state) ?? ''}
              value={count(row.sites).toLocaleString()}
              detail={text(row.note) ?? undefined}
            />
          ))}
        </div>
      </Card>

      <Card title="Read the numbers" subtitle="The same build, with the basis of each layer stated.">
        <table className="mef-table">
          <thead>
            <tr>
              <th>Component</th>
              <th className="mef-num">Gallons</th>
              <th className="mef-num">Dollars</th>
              <th className="mef-num">Share</th>
              <th>Basis</th>
            </tr>
          </thead>
          <tbody>
            {buildup.map((row) => (
              <tr
                key={text(row.step) ?? ''}
                className={text(row.layer) === 'total' ? 'mef-total-row' : undefined}
              >
                <td>{text(row.step)}</td>
                <td className="mef-num">{gallons(num(row.gallons))}</td>
                <td className="mef-num">{money(num(row.dollars))}</td>
                <td className="mef-num">
                  {num(row.share_pct) === null ? NONE : `${(num(row.share_pct) ?? 0).toFixed(1)}%`}
                </td>
                <td>{text(row.basis)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="What this does not tell you">
        <div className="mef-flags">
          {flags.map((row) => (
            <div
              key={text(row.title) ?? ''}
              className={`mef-flag mef-flag-${text(row.tone) ?? 'warn'}`}
            >
              <div className="mef-flag-icon">{text(row.tone) === 'ok' ? '✓' : '!'}</div>
              <div>
                <h3>{text(row.title)}</h3>
                <p>{text(row.body)}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Pipeline" subtitle="Agent health, read before the number.">
        <table className="mef-table">
          <tbody>
            {pipeline.map((row) => (
              <tr key={text(row.field) ?? ''}>
                <td className="mef-field">{text(row.field)}</td>
                <td>{text(row.value) ?? NONE}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
